import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  List as ListIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import PageWithHeader from '../common/PageWithHeader';
import { useRouter } from '../../hooks/useRouter';
import { useAppSelector } from '../../redux/hooks';
import { getAvailableQuartersForSubmission, getDefaultQuarterSelection, generateQuartersForYear, filterQuartersByEligibility, isQuarterSubmissionAllowed, getQuarterSubmissionMessage } from '../../utils/periodUtil';
import {
  useCreatePEARReviewMutation,
  useUpdatePEARReviewMutation,
  useSubmitPEARReviewMutation,
  useManagerEditPEARReviewMutation,
  useGetPEARByIdQuery,
  useGetMyPEARReviewsQuery,
  useGetAdminAllPEARsQuery,
  useDeletePEARReviewMutation,
  PEARReview,
} from '../../redux/pearReviewApi';
import { useGetUsersQuery } from '../../redux/vmsApi';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { useGetMyBSCSubmissionsQuery } from '../../redux/balancedScorecardApi';
import {
  useGet360ReviewsByUserByUserIdQuery,
  FeedbackSurveyWithRelations,
} from '../../redux/vmsApi';
import pearReviewModel, {
  PEARReviewFormType,
  PEARScoreItem,
  PEARHighPerformanceItem,
  PEARKeyFactorCategory,
  PEARKeyFactorSubItem,
  PEARKPIItem,
  PEAROKRKeyResult,
  PEARCoreValueItem,
} from '../forms/models/pearReviewModel';
import type { BalancedScorecard } from './bscTypes';
// toast removed - react-toastify v8 incompatible with React 19

// Score slider marks
const scoreMarks = [
  { value: 0, label: '0' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
];

// Part 2: Letter grade options (A+ to F)
const LETTER_GRADE_OPTIONS = ['A+', 'A', 'A-', 'B+', 'B', 'C+', 'C', 'C-', 'D', 'E', 'F'];

// OKR Scale options (1-5)// Part 2: OKR 1-5 scale options
const OKR_SCALE_OPTIONS = [
  { value: 1, label: '1 - Not Met' },
  { value: 2, label: '2 - Partially Met' },
  { value: 3, label: '3 - Met' },
  { value: 4, label: '4 - Exceeded' },
  { value: 5, label: '5 - Far Exceeded' },
];

// Rating scale info
const RATING_SCALE = [
  { grade: 'A', min: 110, max: 999, description: 'Exceeds expectations', color: '#4caf50' },
  { grade: 'B', min: 95, max: 109.99, description: 'Solid performance', color: '#8bc34a' },
  { grade: 'C', min: 80, max: 94.99, description: 'Partially met expectations', color: '#ff9800' },
  { grade: 'D', min: 0, max: 79.99, description: 'Improvement needed', color: '#f44336' },
];

// 360° Feedback question mapping
const FEEDBACK_360_QUESTIONS: { [key: string]: string } = {
  respectValues: 'Respect the WestProp Values evident in conduct',
  decisionValues: 'Makes decisions in line with WestProp values',
  encourageValues: 'Encourages team members to live the WestProp values',
  improveValues: 'Suggestions to improve living the WestProp values',
  goodDecisions: 'Makes good decisions and trusts the team when delegating tasks',
  highStandards: 'Team is working to a high standard towards departmental goals',
  motivates: 'Motivates others to reach their goals',
  improveLeadership: 'Suggestions to improve leadership skills',
  controlEmotions: 'Controls emotions even in high pressure situations',
  awareness: 'Demonstrates awareness of how actions affect others',
  strongSkills: 'Has strong skills and interacts well with others',
  empathy: 'Demonstrates empathy and responds appropriately to emotional states',
  selfMotivated: 'Is self motivated, striving to achieve goals',
  improveEmotional: 'Suggestions to improve emotional intelligence',
  urgentWork: 'Delivers urgent work without compromising on details',
  meetsDeadlines: 'Meets deadlines and makes the best time use',
  exceedsExpectations: 'Exceeds expectations despite tight schedules',
  improveProductivity: 'Suggestions to improve productivity',
  fullUnderstanding: 'Has full understanding of role and responsibility',
  expertSkills: 'Is an expert and performs job responsibilities skillfully',
  improveJobKnowledge: 'Suggestions to improve job knowledge',
  gracefulDisagree: 'Disagrees gracefully and respectfully',
  adaptsEasily: 'Adapts easily to various situations and different people',
  equalTreatment: 'Equal treatment of everyone, communicates respectfully',
  improveInterpersonal: 'Suggestions to improve interpersonal skills',
  clearGoalsSelf: 'Sets clear goals, objectives and targets for self',
  clearGoalsTeam: 'Sets clear goals for the team',
  performanceReviews: 'Conducts performance reviews timely and objectively',
  manageUnderperformance: 'Proactively manages underperformance',
  improvePerformance: 'Suggestions to improve performance management',
  strength: 'Greatest strength and what to continue doing',
  opportunity: 'Greatest opportunity and what to improve',
};

const FEEDBACK_360_SECTIONS = [
  { title: 'Values', questions: ['respectValues', 'decisionValues', 'encourageValues', 'improveValues'] },
  { title: 'Leadership Skills', questions: ['goodDecisions', 'highStandards', 'motivates', 'improveLeadership'] },
  { title: 'Emotional Intelligence', questions: ['controlEmotions', 'awareness', 'strongSkills', 'empathy', 'selfMotivated', 'improveEmotional'] },
  { title: 'Productivity', questions: ['urgentWork', 'meetsDeadlines', 'exceedsExpectations', 'improveProductivity'] },
  { title: 'Job Knowledge', questions: ['fullUnderstanding', 'expertSkills', 'improveJobKnowledge'] },
  { title: 'Interpersonal Skills', questions: ['gracefulDisagree', 'adaptsEasily', 'equalTreatment', 'improveInterpersonal'] },
  { title: 'Performance Management', questions: ['clearGoalsSelf', 'clearGoalsTeam', 'performanceReviews', 'manageUnderperformance', 'improvePerformance'] },
];

// Helper function to get grade from score
const getGradeFromScore = (score: number): { grade: string; description: string; color: string } => {
  for (const rating of RATING_SCALE) {
    if (score >= rating.min && score <= rating.max) {
      return { grade: rating.grade, description: rating.description, color: rating.color };
    }
  }
  return { grade: 'D', description: 'Improvement needed', color: '#f44336' };
};

// Default ACT/REF Core Values structure
const DEFAULT_CORE_VALUES: PEARCoreValueItem[] = [
  { value: 'ACCOUNTABLE', acronym: 'A', description: 'Accountable to all stakeholders by empowering the team to deliver the best development and investment opportunities', selfRating: '', managerRating: '', group: 'ACT' },
  { value: 'COMMUNITY', acronym: 'C', description: 'Working as one body to better serve the community and enable a sustainable environment for clients to live in WestProp lifestyle estates', selfRating: '', managerRating: '', group: 'ACT' },
  { value: 'TRANSPARENT', acronym: 'T', description: 'Trusted and transparent by being honest and ethical at all times', selfRating: '', managerRating: '', group: 'ACT' },
  { value: 'REPUTABLE', acronym: 'R', description: 'Reputable, respected, responsible, and reliable as an individual and a company', selfRating: '', managerRating: '', group: 'REF' },
  { value: 'EXECUTION', acronym: 'E', description: 'Excellence in execution, being efficient with continued education, and being a shining example', selfRating: '', managerRating: '', group: 'REF' },
  { value: 'FLEXIBLE', acronym: 'F', description: "Focus on clients' needs whilst being flexible and innovative to set market trends", selfRating: '', managerRating: '', group: 'REF' },
];

// Helper to ensure coreValues uses the new ACT/REF structure
const normalizeCoreValues = (coreValues?: PEARCoreValueItem[]): PEARCoreValueItem[] => {
  // If no values or values don't have group field, use defaults
  if (!coreValues || coreValues.length === 0 || !coreValues[0]?.group) {
    return DEFAULT_CORE_VALUES;
  }
  return coreValues;
};

// Score Item Row Component
interface ScoreItemRowProps {
  item: PEARScoreItem;
  index: number;
  onChange: (index: number, score: number) => void;
  disabled?: boolean;
}

const ScoreItemRow: React.FC<ScoreItemRowProps> = ({ item, index, onChange, disabled }) => (
  <TableRow>
    <TableCell sx={{ width: '70%' }}>
      <Typography variant="body2">{item.criterion}</Typography>
    </TableCell>
    <TableCell sx={{ width: '30%' }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Slider
          value={item.score}
          onChange={(_, value) => onChange(index, value as number)}
          min={0}
          max={10}
          step={1}
          marks={scoreMarks}
          valueLabelDisplay="auto"
          disabled={disabled}
          sx={{ minWidth: 120 }}
        />
        <Chip
          label={item.score}
          size="small"
          color={item.score >= 8 ? 'success' : item.score >= 5 ? 'warning' : 'error'}
        />
      </Stack>
    </TableCell>
  </TableRow>
);

// High Performance Item Row Component
interface HighPerformanceRowProps {
  item: PEARHighPerformanceItem;
  index: number;
  onChangeLeft: (index: number, score: number) => void;
  onChangeRight: (index: number, score: number) => void;
  disabled?: boolean;
}

const HighPerformanceRow: React.FC<HighPerformanceRowProps> = ({
  item,
  index,
  onChangeLeft,
  onChangeRight,
  disabled,
}) => (
  <TableRow>
    <TableCell sx={{ width: '25%' }}>
      <Typography variant="body2">{item.attributeLeft}</Typography>
    </TableCell>
    <TableCell sx={{ width: '20%' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Slider
          value={item.scoreLeft}
          onChange={(_, value) => onChangeLeft(index, value as number)}
          min={0}
          max={10}
          step={1}
          valueLabelDisplay="auto"
          disabled={disabled}
          sx={{ minWidth: 80 }}
        />
        <Chip label={item.scoreLeft} size="small" color={item.scoreLeft >= 8 ? 'success' : 'default'} />
      </Stack>
    </TableCell>
    <TableCell sx={{ width: '25%' }}>
      <Typography variant="body2">{item.attributeRight}</Typography>
    </TableCell>
    <TableCell sx={{ width: '20%' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Slider
          value={item.scoreRight}
          onChange={(_, value) => onChangeRight(index, value as number)}
          min={0}
          max={10}
          step={1}
          valueLabelDisplay="auto"
          disabled={disabled}
          sx={{ minWidth: 80 }}
        />
        <Chip label={item.scoreRight} size="small" color={item.scoreRight >= 8 ? 'success' : 'default'} />
      </Stack>
    </TableCell>
  </TableRow>
);

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  maxScore: number;
  currentScore: number;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  subtitle,
  maxScore,
  currentScore,
  children,
  defaultExpanded = true,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const percentage = maxScore > 0 ? (currentScore / maxScore) * 100 : 0;

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => setExpanded(!expanded)}
          sx={{ cursor: 'pointer' }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={`${currentScore} / ${maxScore}`}
              color={percentage >= 80 ? 'success' : percentage >= 50 ? 'warning' : 'error'}
            />
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{ mt: 1, height: 6, borderRadius: 3 }}
          color={percentage >= 80 ? 'success' : percentage >= 50 ? 'warning' : 'error'}
        />
      </CardContent>
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>{children}</CardContent>
      </Collapse>
    </Card>
  );
};

interface PEARReviewFormProps {
  reviewId?: string;
  onSuccess?: () => void;
}

const PEARReviewForm: React.FC<PEARReviewFormProps> = ({ reviewId: propReviewId, onSuccess }) => {
  const router = useRouter();
  const { id: urlReviewId } = useParams<{ id: string }>();
  const reviewId = propReviewId || urlReviewId; // Prefer prop, fallback to URL param
  const user = useAppSelector((state) => state.auth.user);
  const isAdmin = user?.roles?.includes('admin');

  // Check if we came from admin or manager page
  const searchParams = new URLSearchParams(router.search);
  const isFromAdmin = searchParams.get('from') === 'admin';
  const isFromManager = searchParams.get('from') === 'manager';
  const isManagerEdit = isFromManager || isFromAdmin;

  // Fetch existing review if editing
  const { data: existingReview, isLoading: isLoadingReview } = useGetPEARByIdQuery(
    { id: reviewId! },
    { skip: !reviewId }
  );

  // Fetch all user's reviews to check for existing review when period changes
  const { data: myReviews, isLoading: loadingMyReviews } = useGetMyPEARReviewsQuery();

  // Compute available quarters (filter out submitted ones for regular users, show all for admin)
  // 2025 and prior years are closed - only current year (2026+) available for new submissions
  const currentYear = new Date().getFullYear();
  const quarterOptions = useMemo(() => {
    // Admin from admin page can select any quarter from current year only
    if (isAdmin && isFromAdmin) {
      return filterQuartersByEligibility(generateQuartersForYear(currentYear));
    }
    // Regular users only see quarters they haven't submitted (current year only)
    return getAvailableQuartersForSubmission(myReviews);
  }, [isAdmin, isFromAdmin, myReviews, currentYear]);

  // Fetch user's BSC submissions to auto-fill BSC score
  const { data: myBSCSubmissions } = useGetMyBSCSubmissionsQuery();

  // Fetch user's 360° feedback reviews
  const { data: all360Reviews } = useGet360ReviewsByUserByUserIdQuery(
    { userId: user?.id ?? '' },
    { skip: !user?.id }
  );

  // Mutations
  const [createPEAR, { isLoading: isCreating }] = useCreatePEARReviewMutation();
  const [updatePEAR, { isLoading: isUpdating }] = useUpdatePEARReviewMutation();
  const [submitPEAR, { isLoading: isSubmitting }] = useSubmitPEARReviewMutation();
  const [managerEditPEAR, { isLoading: isManagerEditing }] = useManagerEditPEARReviewMutation();
  const [deletePEAR, { isLoading: isDeleting }] = useDeletePEARReviewMutation();

  // Admin-specific: Get all users and all PEAR reviews for employee selection
  const { data: allUsers } = useGetUsersQuery(undefined, { skip: !isAdmin || !isFromAdmin });
  const { data: adminAllPEARs, refetch: refetchAdminPEARs } = useGetAdminAllPEARsQuery(undefined, { skip: !isAdmin || !isFromAdmin });

  // Admin state: selected employee
  const [adminSelectedEmployeeId, setAdminSelectedEmployeeId] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<PEARReviewFormType>(() => {
    const initial = existingReview || pearReviewModel.initialValues;
    return {
      ...initial,
      coreValues: normalizeCoreValues(initial.coreValues),
    };
  });

  // Find BSC for the current review period (must be after formData is defined)
  const bscForPeriod = useMemo((): BalancedScorecard | undefined => {
    if (!formData.reviewPeriod || !myBSCSubmissions) return undefined;
    return myBSCSubmissions.find(bsc => bsc.reviewPeriod === formData.reviewPeriod);
  }, [formData.reviewPeriod, myBSCSubmissions]);

  // Check if quarter has ended and submission is allowed
  const canSubmitQuarter = useMemo(() => {
    return isQuarterSubmissionAllowed(formData.reviewPeriod || '');
  }, [formData.reviewPeriod]);

  // Get the quarter submission restriction message
  const quarterSubmissionMessage = useMemo(() => {
    return getQuarterSubmissionMessage(formData.reviewPeriod || '');
  }, [formData.reviewPeriod]);

  // Find 360° feedback for the current review period
  const feedback360ForPeriod = useMemo((): FeedbackSurveyWithRelations[] => {
    if (!formData.reviewPeriod || !all360Reviews) return [];
    return all360Reviews.filter(review => review.reviewPeriod === formData.reviewPeriod);
  }, [formData.reviewPeriod, all360Reviews]);

  // Helper functions for 360 feedback rating display (1-10 scale)
  const getColorForRating = (rating: number): string => {
    if (rating >= 9) return '#1b5e20';
    if (rating >= 8) return '#2e7d32';
    if (rating >= 7) return '#4caf50';
    if (rating >= 6) return '#8bc34a';
    if (rating >= 5) return '#cddc39';
    if (rating >= 4) return '#ffc107';
    if (rating >= 3) return '#ff9800';
    if (rating >= 2) return '#ff5722';
    return '#f44336';
  };

  const getLabelForRating = (rating: number): string => {
    if (rating >= 9.5) return 'Outstanding';
    if (rating >= 8.5) return 'Excellent';
    if (rating >= 7.5) return 'Very Good';
    if (rating >= 6.5) return 'Good';
    if (rating >= 5.5) return 'Above Avg';
    if (rating >= 4.5) return 'Average';
    if (rating >= 3.5) return 'Below Avg';
    if (rating >= 2.5) return 'Poor';
    return 'Very Poor';
  };

  // Process 360° feedback into summary data
  const feedback360Summary = useMemo(() => {
    if (feedback360ForPeriod.length === 0) return null;

    const processSection = (sectionQuestions: string[]) => {
      const ratingQuestions = sectionQuestions.filter(q => !q.includes('improve') && !['strength', 'opportunity'].includes(q));
      const textQuestions = sectionQuestions.filter(q => q.includes('improve') || ['strength', 'opportunity'].includes(q));
      
      const ratingData = ratingQuestions.map(questionId => {
        const responses = feedback360ForPeriod.flatMap(review => 
          review.responses.filter(r => r.questionId === questionId)
        );
        
        // Calculate average rating (with backwards compatibility for boolean responses)
        const ratings: number[] = [];
        responses.forEach(r => {
          if (r.responseRating !== undefined) {
            ratings.push(r.responseRating);
          } else if (r.responseBoolean !== undefined) {
            // Backwards compatibility: true = 10, false = 1
            ratings.push(r.responseBoolean ? 10 : 1);
          }
        });
        
        const avgRating = ratings.length > 0 
          ? ratings.reduce((a, b) => a + b, 0) / ratings.length 
          : 0;
        
        return {
          questionId,
          question: FEEDBACK_360_QUESTIONS[questionId] || questionId,
          avgRating,
          responseCount: ratings.length,
        };
      });

      // Calculate section average
      const sectionAvg = ratingData.length > 0 
        ? ratingData.reduce((sum, item) => sum + item.avgRating, 0) / ratingData.length 
        : 0;

      const textResponses = textQuestions.flatMap(questionId => {
        const responses = feedback360ForPeriod.flatMap(review =>
          review.responses.filter(r => r.questionId === questionId && r.responseText)
        );
        return responses.map(r => ({
          questionId,
          question: FEEDBACK_360_QUESTIONS[questionId] || questionId,
          text: r.responseText!,
        }));
      });

      return { ratingData, textResponses, sectionAvg };
    };

    // Calculate overall average
    const sectionResults = FEEDBACK_360_SECTIONS.map(section => ({
      title: section.title,
      ...processSection(section.questions),
    }));
    
    const overallAvg = sectionResults.length > 0
      ? sectionResults.reduce((sum, s) => sum + s.sectionAvg, 0) / sectionResults.length
      : 0;

    return {
      totalReviewers: feedback360ForPeriod.length,
      overallAvg,
      sections: sectionResults,
    };
  }, [feedback360ForPeriod]);
  
  // Track current review ID for autosave (may be created from new)
  const [currentReviewId, setCurrentReviewId] = useState<string | undefined>(reviewId);
  // Track review status locally (separate from form data to prevent it being overwritten)
  const [reviewStatus, setReviewStatus] = useState<string | undefined>(existingReview?.status);
  const isAutosaving = useRef(false);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Handle review period change - load existing or reset
  const handleReviewPeriodChange = useCallback((period: string) => {
    // Check if there's an existing review for this period
    const existingForPeriod = myReviews?.find(r => r.reviewPeriod === period);
    
    if (existingForPeriod) {
      // Load the existing review, ensuring coreValues uses new structure
      setFormData({
        ...existingForPeriod,
        coreValues: normalizeCoreValues(existingForPeriod.coreValues),
      });
      setCurrentReviewId(existingForPeriod.id);
      setReviewStatus(existingForPeriod.status);
      setLastSavedAt(null);
      // Note: Existing review loaded for this period
    } else {
      // Reset to initial values with new period and user info
      setFormData({
        ...pearReviewModel.initialValues,
        reviewPeriod: period,
        employeeName: user?.name || '',
        designation: user?.jobTitle || '',
        department: user?.department || '',
        coreValues: DEFAULT_CORE_VALUES,
      });
      setCurrentReviewId(undefined);
      setReviewStatus(undefined);
      setLastSavedAt(null);
    }
  }, [myReviews, user]);

  // Check if review period is set
  const isReviewPeriodSet = Boolean(formData.reviewPeriod);

  // Admin: Filter PEAR reviews for the selected period
  const adminPEARsForPeriod = useMemo(() => {
    if (!isAdmin || !isFromAdmin || !adminAllPEARs || !formData.reviewPeriod) return [];
    return adminAllPEARs.filter(p => p.reviewPeriod === formData.reviewPeriod);
  }, [isAdmin, isFromAdmin, adminAllPEARs, formData.reviewPeriod]);

  // Admin: Get selected employee's PEAR review
  const adminSelectedEmployeePEAR = useMemo((): PEARReview | undefined => {
    if (!adminSelectedEmployeeId || !adminPEARsForPeriod.length) return undefined;
    return adminPEARsForPeriod.find(p => p.employeeId === adminSelectedEmployeeId);
  }, [adminSelectedEmployeeId, adminPEARsForPeriod]);

  // Admin: Get employees list with PEAR status for dropdown
  const adminEmployeeOptions = useMemo(() => {
    if (!isAdmin || !isFromAdmin || !allUsers) return [];
    return allUsers
      .filter(u => u.id !== user?.id) // Exclude current user
      .map(u => {
        const pearReview = adminPEARsForPeriod.find(p => p.employeeId === u.id);
        return {
          id: u.id,
          name: u.name || u.email,
          hasPEAR: !!pearReview,
          pearId: pearReview?.id,
          pearStatus: pearReview?.status,
          pearScore: pearReview?.overallScore,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [isAdmin, isFromAdmin, allUsers, adminPEARsForPeriod, user?.id]);

  // Admin: Handle employee selection
  const handleAdminEmployeeChange = useCallback((employeeId: string) => {
    setAdminSelectedEmployeeId(employeeId);
    
    if (!employeeId) {
      // Reset to initial form state
      setFormData({
        ...pearReviewModel.initialValues,
        reviewPeriod: formData.reviewPeriod,
        coreValues: DEFAULT_CORE_VALUES,
      });
      setCurrentReviewId(undefined);
      setReviewStatus(undefined);
      return;
    }

    const pear = adminPEARsForPeriod.find(p => p.employeeId === employeeId);
    if (pear) {
      setFormData({
        ...pear,
        coreValues: normalizeCoreValues(pear.coreValues),
      });
      setCurrentReviewId(pear.id);
      setReviewStatus(pear.status);
    } else {
      // Employee has no PEAR for this period
      const selectedUser = allUsers?.find(u => u.id === employeeId);
      setFormData({
        ...pearReviewModel.initialValues,
        reviewPeriod: formData.reviewPeriod,
        employeeName: selectedUser?.name || '',
        coreValues: DEFAULT_CORE_VALUES,
      });
      setCurrentReviewId(undefined);
      setReviewStatus(undefined);
    }
  }, [adminPEARsForPeriod, allUsers, formData.reviewPeriod]);

  // Admin: Handle delete PEAR
  const handleAdminDeletePEAR = useCallback((pearId: string, employeeName: string) => {
    confirmAlert({
      title: 'Delete PEAR Review',
      message: `Are you sure you want to delete the PEAR Review for ${employeeName}?`,
      buttons: [
        {
          label: 'Yes, Delete',
          onClick: async () => {
            try {
              await deletePEAR({ id: pearId }).unwrap();
              // Reset selection after delete
              setAdminSelectedEmployeeId('');
              setFormData({
                ...pearReviewModel.initialValues,
                reviewPeriod: formData.reviewPeriod,
                coreValues: DEFAULT_CORE_VALUES,
              });
              setCurrentReviewId(undefined);
              setReviewStatus(undefined);
              refetchAdminPEARs();
              alert('PEAR Review deleted successfully!');
            } catch (error: any) {
              console.error('Failed to delete PEAR Review:', error);
              const statusCode = error?.status || error?.data?.error?.statusCode || '';
              const errorType = error?.data?.error?.name || error?.name || '';
              const errorMessage = error?.data?.error?.message 
                || error?.data?.message 
                || error?.message 
                || 'Unknown error occurred';
              
              let fullMessage = `Failed to delete PEAR Review:\n\n${errorMessage}`;
              if (statusCode || errorType) fullMessage += `\n\n(${[errorType, statusCode].filter(Boolean).join(' - ')})`;
              
              alert(fullMessage);
            }
          },
        },
        {
          label: 'Cancel',
          onClick: () => {},
        },
      ],
    });
  }, [deletePEAR, formData.reviewPeriod, refetchAdminPEARs]);

  // Helper to clean payload - remove fields that backend doesn't accept or auto-generates
  const cleanPayload = (data: PEARReviewFormType) => {
    // Destructure to remove fields that shouldn't be sent to the API
    const {
      // Remove ID fields - these are auto-generated
      id,
      // Remove audit fields - backend manages these
      createdAt,
      updatedAt,
      createdBy,
      updatedBy,
      // Remove computed total fields - backend recalculates these
      qualityOfWorkTotal,
      workHabitsTotal,
      jobKnowledgeTotal,
      highPerformanceCultureTotal,
      functionalSkillsTotal,
      interpersonalSkillsTotal,
      leadershipProblemSolvingTotal,
      interpersonalTotal,
      rawTotal,
      overallScore,
      finalGrade,
      ratingDescription,
      // Remove manager fields - only manager should set these via manager-edit endpoint
      managerId,
      managerName,
      managerEditedAt,
      // These need conditional handling
      joiningDate,
      okrObjectiveChallenge,
      overallManagerRating,
      // Keep the rest
      ...rest
    } = data as any;
    
    return {
      ...rest,
      ...(joiningDate ? { joiningDate } : {}),
      // Only include numeric fields if they have valid values
      ...(typeof okrObjectiveChallenge === 'number' ? { okrObjectiveChallenge } : {}),
      ...(typeof overallManagerRating === 'number' ? { overallManagerRating } : {}),
      employeeId: user?.id,
    };
  };

  // Update form data when existing review loads
  useEffect(() => {
    if (existingReview) {
      setFormData({
        ...existingReview,
        coreValues: normalizeCoreValues(existingReview.coreValues),
      });
      setCurrentReviewId(existingReview.id);
      setReviewStatus(existingReview.status);
    }
  }, [existingReview]);

  // Load review from URL query param (?period=...) - only for current user's reviews (no reviewId)
  useEffect(() => {
    // Skip if viewing a specific review by ID (e.g., admin viewing another user's review)
    if (reviewId) return;
    
    const searchParams = new URLSearchParams(router.search);
    const periodParam = searchParams.get('period');
    
    if (periodParam && myReviews) {
      // Only trigger once when myReviews loads and we have a period param
      handleReviewPeriodChange(periodParam);
    }
  }, [reviewId, myReviews, router.search, handleReviewPeriodChange]);

  // Set default quarter when reviews load and no period is selected
  useEffect(() => {
    // Skip if editing specific review or if period already set
    if (reviewId || formData.reviewPeriod) return;
    // Skip if still loading or no URL period param to check
    if (loadingMyReviews) return;
    
    const searchParams = new URLSearchParams(router.search);
    const periodParam = searchParams.get('period');
    
    // Only auto-select if no period in URL
    if (!periodParam && quarterOptions.length > 0) {
      const defaultQuarter = getDefaultQuarterSelection(quarterOptions);
      if (defaultQuarter) {
        handleReviewPeriodChange(defaultQuarter);
      }
    }
  }, [reviewId, formData.reviewPeriod, loadingMyReviews, quarterOptions, router.search, handleReviewPeriodChange]);

  // Auto-populate BSC score when BSC is found for the period
  useEffect(() => {
    if (bscForPeriod) {
      const bscScore = bscForPeriod.overallPerformance ?? bscForPeriod.totalWeightedScore;
      if (bscScore !== undefined && bscScore !== formData.bscScore) {
        setFormData(prev => ({ ...prev, bscScore }));
      }
    }
  }, [bscForPeriod, formData.bscScore]);

  // Autosave function - non-blocking, fire and forget
  const performAutosave = useCallback((data: PEARReviewFormType, id?: string) => {
    if (isAutosaving.current) return;
    if (!data.reviewPeriod) return; // Don't autosave without review period
    
    isAutosaving.current = true;
    
    // Use setTimeout(0) to push to macro task queue, preventing UI blocking
    setTimeout(async () => {
      try {
        const payload = { ...cleanPayload(data), status: 'draft' as const };
        
        if (id) {
          await updatePEAR({ id, body: payload }).unwrap();
        } else {
          const created = await createPEAR(payload).unwrap();
          setCurrentReviewId(created.id);
        }
        setLastSavedAt(new Date());
      } catch (error) {
        console.error('Autosave failed:', error);
      } finally {
        isAutosaving.current = false;
      }
    }, 0);
  }, [createPEAR, updatePEAR, user?.id]);

  // Use ref to track latest form data without causing re-renders
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Autosave effect - longer debounce (5 seconds) for better UX
  useEffect(() => {
    // Don't autosave if view only (submitted/approved) or no review period set
    if (reviewStatus === 'submitted' || reviewStatus === 'approved' || !isReviewPeriodSet) return;
    
    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    // Set new timeout for autosave (5 seconds debounce for smoother typing)
    autosaveTimeoutRef.current = setTimeout(() => {
      performAutosave(formDataRef.current, currentReviewId);
    }, 5000);
    
    // Cleanup
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [formData, currentReviewId, isReviewPeriodSet, reviewStatus, performAutosave]);

  // Calculate totals
  const totals = useMemo(() => {
    const qualityOfWorkTotal = formData.qualityOfWork.reduce((sum, item) => sum + item.score, 0);
    const workHabitsTotal = formData.workHabits.reduce((sum, item) => sum + item.score, 0);
    const jobKnowledgeTotal = formData.jobKnowledge.reduce((sum, item) => sum + item.score, 0);
    const highPerformanceCultureTotal = formData.highPerformanceCulture.reduce(
      (sum, item) => sum + item.scoreLeft + item.scoreRight,
      0
    );
    const functionalSkillsTotal =
      qualityOfWorkTotal + workHabitsTotal + jobKnowledgeTotal + highPerformanceCultureTotal;

    const interpersonalSkillsTotal = formData.interpersonalSkills.reduce(
      (sum, item) => sum + item.score,
      0
    );
    const leadershipProblemSolvingTotal = formData.leadershipProblemSolving.reduce(
      (sum, item) => sum + item.score,
      0
    );
    const interpersonalTotal = interpersonalSkillsTotal + leadershipProblemSolvingTotal;

    const rawTotal = functionalSkillsTotal + interpersonalTotal;
    const overallScore = (rawTotal / 300) * 110;
    const gradeInfo = getGradeFromScore(overallScore);

    return {
      qualityOfWorkTotal,
      workHabitsTotal,
      jobKnowledgeTotal,
      highPerformanceCultureTotal,
      functionalSkillsTotal,
      interpersonalSkillsTotal,
      leadershipProblemSolvingTotal,
      interpersonalTotal,
      rawTotal,
      overallScore,
      ...gradeInfo,
    };
  }, [formData]);

  // Handlers
  const handleFieldChange = (field: keyof PEARReviewFormType, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleScoreChange = (
    field: 'qualityOfWork' | 'workHabits' | 'jobKnowledge' | 'interpersonalSkills' | 'leadershipProblemSolving',
    index: number,
    score: number
  ) => {
    const updated = [...formData[field]];
    updated[index] = { ...updated[index], score };
    setFormData({ ...formData, [field]: updated });
  };

  const handleHighPerformanceChange = (
    index: number,
    side: 'left' | 'right',
    score: number
  ) => {
    const updated = [...formData.highPerformanceCulture];
    if (side === 'left') {
      updated[index] = { ...updated[index], scoreLeft: score };
    } else {
      updated[index] = { ...updated[index], scoreRight: score };
    }
    setFormData({ ...formData, highPerformanceCulture: updated });
  };

  const handleSave = async (asDraft = true) => {
    if (!formData.reviewPeriod) {
      alert('Please select a review period first');
      return;
    }
    
    try {
      const payload = {
        ...cleanPayload(formData),
        status: (asDraft ? 'draft' : 'submitted') as 'draft' | 'submitted',
      };

      if (isManagerEdit && currentReviewId) {
        // Manager editing employee's review
        await managerEditPEAR({ id: currentReviewId, body: payload }).unwrap();
        alert('Manager edits saved successfully!');
        router.push('/reviews/pear/admin');
        return;
      }

      if (currentReviewId) {
        await updatePEAR({ id: currentReviewId, body: payload }).unwrap();
      } else {
        const created = await createPEAR(payload).unwrap();
        setCurrentReviewId(created.id);
      }

      if (onSuccess) {
        onSuccess();
      } else {
        router.push('/reviews/pear/dashboard');
      }
    } catch (error: any) {
      console.error('Error saving PEAR Review:', error);
      const statusCode = error?.status || error?.data?.error?.statusCode || '';
      const errorType = error?.data?.error?.name || error?.name || '';
      const errorMessage = error?.data?.error?.message 
        || error?.data?.message 
        || error?.message 
        || 'Unknown error occurred';
      const errorDetails = error?.data?.error?.details 
        || error?.data?.details 
        || '';
      
      let fullMessage = `Failed to save PEAR Review:\n\n${errorMessage}`;
      if (errorDetails) fullMessage += `\n\nDetails: ${errorDetails}`;
      if (statusCode || errorType) fullMessage += `\n\n(${[errorType, statusCode].filter(Boolean).join(' - ')})`;
      
      alert(fullMessage);
    }
  };

  const handleSubmit = async () => {
    if (!formData.reviewPeriod) {
      alert('Please select a review period first');
      return;
    }

    // Check if quarter has ended - submission only allowed after quarter ends
    if (!canSubmitQuarter) {
      alert(`Cannot submit yet.\n\n${quarterSubmissionMessage}\n\nYou can save your work as a draft and submit once the quarter has ended.`);
      return;
    }
    
    const idToSubmit = currentReviewId;
    
    if (!idToSubmit) {
      // First create, then submit
      try {
        const created = await createPEAR(cleanPayload(formData)).unwrap();
        setCurrentReviewId(created.id);
        const submitted = await submitPEAR({ id: created.id }).unwrap();
        setReviewStatus(submitted.status); // Update local status to prevent further edits
        alert('PEAR Review submitted successfully!');
        // Stay on page - form will now be read-only
      } catch (error: any) {
        console.error('Error submitting PEAR Review:', error);
        const statusCode = error?.status || error?.data?.error?.statusCode || '';
        const errorType = error?.data?.error?.name || error?.name || '';
        const errorMessage = error?.data?.error?.message 
          || error?.data?.message 
          || error?.message 
          || 'Unknown error occurred';
        const errorDetails = error?.data?.error?.details 
          || error?.data?.details 
          || '';
        
        let fullMessage = `Failed to submit PEAR Review:\n\n${errorMessage}`;
        if (errorDetails) fullMessage += `\n\nDetails: ${errorDetails}`;
        if (statusCode || errorType) fullMessage += `\n\n(${[errorType, statusCode].filter(Boolean).join(' - ')})`;
        
        alert(fullMessage);
      }
    } else {
      try {
        await updatePEAR({ id: idToSubmit, body: cleanPayload(formData) }).unwrap();
        const submitted = await submitPEAR({ id: idToSubmit }).unwrap();
        setReviewStatus(submitted.status); // Update local status to prevent further edits
        alert('PEAR Review submitted successfully!');
        // Stay on page - form will now be read-only
      } catch (error: any) {
        console.error('Error submitting PEAR Review:', error);
        const statusCode = error?.status || error?.data?.error?.statusCode || '';
        const errorType = error?.data?.error?.name || error?.name || '';
        const errorMessage = error?.data?.error?.message 
          || error?.data?.message 
          || error?.message 
          || 'Unknown error occurred';
        const errorDetails = error?.data?.error?.details 
          || error?.data?.details 
          || '';
        
        let fullMessage = `Failed to submit PEAR Review:\n\n${errorMessage}`;
        if (errorDetails) fullMessage += `\n\nDetails: ${errorDetails}`;
        if (statusCode || errorType) fullMessage += `\n\n(${[errorType, statusCode].filter(Boolean).join(' - ')})`;
        
        alert(fullMessage);
      }
    }
  };

  const isLoading = isCreating || isUpdating || isSubmitting || isManagerEditing || isLoadingReview || isDeleting;
  // Form is view-only if: submitted/approved (unless manager editing), OR viewing another user's review (without manager access)
  const isViewingOtherUser = existingReview && existingReview.employeeId !== user?.id;
  const canManagerEdit = isManagerEdit && isViewingOtherUser;
  const isViewOnly = (reviewStatus === 'submitted' || reviewStatus === 'approved') && !canManagerEdit;
  const isFormDisabled = isViewOnly || !isReviewPeriodSet;
  // Submission is only allowed for your own PEAR (managers can edit but not submit on behalf)
  const canSubmitPEAR = !existingReview || existingReview.employeeId === user?.id;

  // Admin view layout (similar to AdminBSC)
  if (isAdmin && isFromAdmin) {
    return (
      <PageWithHeader>
        <Box sx={{ p: { xs: 2, md: 5 } }}>
          {/* Back Button */}
          <Button color="primary" onClick={() => router.push('/reviews/admin')} sx={{ mb: 2 }}>
            <ArrowBackIcon />
            <Typography color="primary" align="left" sx={{ ml: 1 }}>
              Admin Dashboard
            </Typography>
          </Button>

          {/* Filters Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Review Period</InputLabel>
                <Select
                  value={formData.reviewPeriod}
                  label="Review Period"
                  onChange={(e: SelectChangeEvent) => handleReviewPeriodChange(e.target.value)}
                >
                  {quarterOptions.map((quarter) => (
                    <MenuItem key={quarter} value={quarter}>
                      {quarter}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 300 }}>
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={adminSelectedEmployeeId}
                  label="Select Employee"
                  onChange={(e: SelectChangeEvent) => handleAdminEmployeeChange(e.target.value)}
                  disabled={!formData.reviewPeriod}
                >
                  <MenuItem value="">
                    <em>-- Select an employee --</em>
                  </MenuItem>
                  {adminEmployeeOptions.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Typography>{emp.name}</Typography>
                        {emp.hasPEAR ? (
                          <Chip
                            label={`${emp.pearStatus} - ${emp.pearScore?.toFixed(0)}%`}
                            size="small"
                            color={emp.pearStatus === 'approved' ? 'success' : emp.pearStatus === 'submitted' ? 'info' : 'default'}
                          />
                        ) : (
                          <Chip label="No PEAR" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Delete button for selected PEAR */}
              {adminSelectedEmployeeId && adminSelectedEmployeePEAR && (
                <Tooltip title="Delete PEAR Review">
                  <IconButton
                    color="error"
                    onClick={() => handleAdminDeletePEAR(adminSelectedEmployeePEAR.id, adminSelectedEmployeePEAR.employeeName || 'Employee')}
                    disabled={isDeleting}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Paper>

          {/* Show message when no employee selected */}
          {!adminSelectedEmployeeId && (
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Select a <strong>Review Period</strong> and an <strong>Employee</strong> to view or manage their PEAR Review.
              </Typography>
            </Paper>
          )}

          {/* Employee PEAR Details */}
          {adminSelectedEmployeeId && adminSelectedEmployeePEAR && (
            <Paper elevation={3} sx={{ p: 4 }}>
              {/* Header with employee info and status */}
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    PEAR Review
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {adminSelectedEmployeePEAR.employeeName || 'Employee'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={adminSelectedEmployeePEAR.status || 'draft'}
                    color={
                      adminSelectedEmployeePEAR.status === 'approved' ? 'success' :
                      adminSelectedEmployeePEAR.status === 'submitted' ? 'info' :
                      adminSelectedEmployeePEAR.status === 'rejected' ? 'error' : 'default'
                    }
                    size="medium"
                  />
                  <Chip
                    label={`Grade: ${adminSelectedEmployeePEAR.finalGrade || totals.grade || '-'}`}
                    sx={{
                      bgcolor: totals.color,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                </Stack>
              </Stack>

              {/* Summary Cards */}
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={4} flexWrap="wrap">
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      Period
                    </Typography>
                    <Typography fontWeight="bold">{adminSelectedEmployeePEAR.reviewPeriod || '-'}</Typography>
                  </Paper>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      Overall Score
                    </Typography>
                    <Typography fontWeight="bold">
                      {adminSelectedEmployeePEAR.overallScore?.toFixed(1) ?? totals.overallScore.toFixed(1)}%
                    </Typography>
                  </Paper>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography fontWeight="bold">{adminSelectedEmployeePEAR.department || '-'}</Typography>
                  </Paper>
                </Box>
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 23%' } }}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      Designation
                    </Typography>
                    <Typography fontWeight="bold">{adminSelectedEmployeePEAR.designation || '-'}</Typography>
                  </Paper>
                </Box>
              </Stack>

              {/* Skills Breakdown */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Skills Breakdown
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Score</strong></TableCell>
                      <TableCell align="right"><strong>Max</strong></TableCell>
                      <TableCell align="right"><strong>Percentage</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Quality of Work</TableCell>
                      <TableCell align="right">{totals.qualityOfWorkTotal}</TableCell>
                      <TableCell align="right">30</TableCell>
                      <TableCell align="right">{((totals.qualityOfWorkTotal / 30) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Work Habits</TableCell>
                      <TableCell align="right">{totals.workHabitsTotal}</TableCell>
                      <TableCell align="right">60</TableCell>
                      <TableCell align="right">{((totals.workHabitsTotal / 60) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Job Knowledge</TableCell>
                      <TableCell align="right">{totals.jobKnowledgeTotal}</TableCell>
                      <TableCell align="right">30</TableCell>
                      <TableCell align="right">{((totals.jobKnowledgeTotal / 30) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>High Performance Culture</TableCell>
                      <TableCell align="right">{totals.highPerformanceCultureTotal}</TableCell>
                      <TableCell align="right">80</TableCell>
                      <TableCell align="right">{((totals.highPerformanceCultureTotal / 80) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Functional Skills Total</strong></TableCell>
                      <TableCell align="right"><strong>{totals.functionalSkillsTotal}</strong></TableCell>
                      <TableCell align="right"><strong>200</strong></TableCell>
                      <TableCell align="right"><strong>{((totals.functionalSkillsTotal / 200) * 100).toFixed(0)}%</strong></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Interpersonal Skills</TableCell>
                      <TableCell align="right">{totals.interpersonalSkillsTotal}</TableCell>
                      <TableCell align="right">60</TableCell>
                      <TableCell align="right">{((totals.interpersonalSkillsTotal / 60) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Leadership & Problem Solving</TableCell>
                      <TableCell align="right">{totals.leadershipProblemSolvingTotal}</TableCell>
                      <TableCell align="right">40</TableCell>
                      <TableCell align="right">{((totals.leadershipProblemSolvingTotal / 40) * 100).toFixed(0)}%</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Interpersonal Total</strong></TableCell>
                      <TableCell align="right"><strong>{totals.interpersonalTotal}</strong></TableCell>
                      <TableCell align="right"><strong>100</strong></TableCell>
                      <TableCell align="right"><strong>{((totals.interpersonalTotal / 100) * 100).toFixed(0)}%</strong></TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell><strong>Grand Total</strong></TableCell>
                      <TableCell align="right"><strong>{totals.rawTotal}</strong></TableCell>
                      <TableCell align="right"><strong>300</strong></TableCell>
                      <TableCell align="right"><strong>{totals.overallScore.toFixed(1)}%</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Employee has no PEAR */}
          {adminSelectedEmployeeId && !adminSelectedEmployeePEAR && (
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="warning.main" fontWeight="bold">
                No PEAR Review Found
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                This employee has not submitted a PEAR Review for {formData.reviewPeriod}.
              </Typography>
            </Paper>
          )}
        </Box>
      </PageWithHeader>
    );
  }

  // Regular user view (existing layout)
  return (
    <PageWithHeader>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={() => router.push(isManagerEdit ? '/reviews/pear/admin' : '/reviews')}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {isManagerEdit ? `PEAR Review - ${existingReview?.employeeName || 'Employee'}` : 'PEAR Review'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  {isManagerEdit 
                    ? 'Manager review and feedback'
                    : 'Quarterly Performance Evaluation Assessment Review'
                  }
                </Typography>
                {lastSavedAt && !isViewOnly && (
                  <Chip
                    label={`Auto-saved ${lastSavedAt.toLocaleTimeString()}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ListIcon />}
              onClick={() => router.push('/reviews/pear/dashboard')}
            >
              My Reviews
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => handleSave(true)}
              disabled={isLoading || isViewOnly || !isReviewPeriodSet}
            >
              Save Draft
            </Button>
            <Tooltip title={!canSubmitQuarter ? quarterSubmissionMessage : (!canSubmitPEAR ? 'Only the employee can submit their own PEAR Review' : '')} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={isLoading || isViewOnly || !isReviewPeriodSet || !canSubmitQuarter || !canSubmitPEAR}
                >
                  Submit
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Warning if review period not set */}
        {!isReviewPeriodSet && !isViewOnly && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {quarterOptions.length === 0
                ? 'All available quarterly PEAR reviews have been submitted.'
                : <>Please select a <strong>Review Period</strong> to enable the form. Your progress will be auto-saved as a draft.</>
              }
            </Typography>
          </Alert>
        )}

        {/* Quarter Submission Restriction Warning */}
        {isReviewPeriodSet && !canSubmitQuarter && !isViewOnly && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Submission Not Yet Available:</strong> {quarterSubmissionMessage}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You can continue to prepare your PEAR Review and save it as a draft. Submission will be enabled once the quarter ends.
            </Typography>
          </Alert>
        )}

        {/* Status indicator for submitted/approved reviews */}
        {isViewOnly && (
          <Alert 
            severity={reviewStatus === 'approved' ? 'success' : 'info'} 
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              {isViewingOtherUser
                ? `Viewing ${existingReview?.employeeName || 'employee'}'s PEAR Review (read-only).`
                : reviewStatus === 'approved' 
                  ? 'This PEAR Review has been approved and cannot be edited.'
                  : 'This PEAR Review has been submitted for approval and cannot be edited.'}
            </Typography>
          </Alert>
        )}

        {/* Rating Scale Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Rating Scale
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            {RATING_SCALE.map((rating) => (
              <Chip
                key={rating.grade}
                label={`${rating.grade}: ${rating.min}%+ - ${rating.description}`}
                size="small"
                sx={{ bgcolor: rating.color, color: 'white' }}
              />
            ))}
          </Stack>
        </Alert>

        {/* Score Summary Card */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, bgcolor: totals.color + '15' }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Stack alignItems="center">
                <Typography variant="h2" fontWeight="bold" color={totals.color}>
                  {totals.overallScore.toFixed(1)}%
                </Typography>
                <Chip
                  label={`Grade ${totals.grade}: ${totals.description}`}
                  sx={{ bgcolor: totals.color, color: 'white', mt: 1 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Functional Skills
                  </Typography>
                  <Typography variant="h5">
                    {totals.functionalSkillsTotal} / 200
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Interpersonal Skills
                  </Typography>
                  <Typography variant="h5">
                    {totals.interpersonalTotal} / 100
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Raw Total
                  </Typography>
                  <Typography variant="h5">
                    {totals.rawTotal} / 300
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>

        {/* Employee Info Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>Review Period *</InputLabel>
                  <Select
                    value={formData.reviewPeriod}
                    label="Review Period *"
                    onChange={(e: SelectChangeEvent) =>
                      handleReviewPeriodChange(e.target.value)
                    }
                    disabled={isViewOnly}
                  >
                    {quarterOptions.map((quarter) => (
                      <MenuItem key={quarter} value={quarter}>
                        {quarter}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Employee Name"
                  value={formData.employeeName || user?.name || ''}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={formData.designation || user?.jobTitle || ''}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Department"
                  value={formData.department || user?.department || ''}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Reports To"
                  value={formData.reportsTo || ''}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Joining Date"
                  value={formData.joiningDate || ''}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* FUNCTIONAL SKILLS (200 max) */}
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, mt: 4 }}>
          FUNCTIONAL SKILLS
          <Chip label="Max: 200" size="small" sx={{ ml: 2 }} />
        </Typography>

        {/* Quality of Work */}
        <CollapsibleSection
          title="Quality of Work"
          maxScore={30}
          currentScore={totals.qualityOfWorkTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Score (0-10)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.qualityOfWork.map((item, index) => (
                  <ScoreItemRow
                    key={index}
                    item={item}
                    index={index}
                    onChange={(i, score) => handleScoreChange('qualityOfWork', i, score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* Work Habits */}
        <CollapsibleSection
          title="Work Habits"
          maxScore={40}
          currentScore={totals.workHabitsTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Score (0-10)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.workHabits.map((item, index) => (
                  <ScoreItemRow
                    key={index}
                    item={item}
                    index={index}
                    onChange={(i, score) => handleScoreChange('workHabits', i, score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* Job Knowledge */}
        <CollapsibleSection
          title="Job Knowledge, Duty & Skills"
          maxScore={30}
          currentScore={totals.jobKnowledgeTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Score (0-10)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.jobKnowledge.map((item, index) => (
                  <ScoreItemRow
                    key={index}
                    item={item}
                    index={index}
                    onChange={(i, score) => handleScoreChange('jobKnowledge', i, score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* High Performance Culture */}
        <CollapsibleSection
          title="High Performance Culture"
          subtitle="Performance of Core Duty / Function"
          maxScore={100}
          currentScore={totals.highPerformanceCultureTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Attribute</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Attribute</TableCell>
                  <TableCell>Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.highPerformanceCulture.map((item, index) => (
                  <HighPerformanceRow
                    key={index}
                    item={item}
                    index={index}
                    onChangeLeft={(i, score) => handleHighPerformanceChange(i, 'left', score)}
                    onChangeRight={(i, score) => handleHighPerformanceChange(i, 'right', score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* INTERPERSONAL SKILLS (100 max) */}
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, mt: 4 }}>
          INTERPERSONAL SKILLS
          <Chip label="Max: 100" size="small" sx={{ ml: 2 }} />
        </Typography>

        {/* Interpersonal Skills/Relations/Behaviour */}
        <CollapsibleSection
          title="Interpersonal Skills/Relations/Behaviour"
          maxScore={70}
          currentScore={totals.interpersonalSkillsTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Score (0-10)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.interpersonalSkills.map((item, index) => (
                  <ScoreItemRow
                    key={index}
                    item={item}
                    index={index}
                    onChange={(i, score) => handleScoreChange('interpersonalSkills', i, score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* Leadership/Problem-Solving */}
        <CollapsibleSection
          title="Leadership/Problem-Solving"
          subtitle="Out of 30 Marks"
          maxScore={30}
          currentScore={totals.leadershipProblemSolvingTotal}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Criteria</TableCell>
                  <TableCell>Score (0-10)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {formData.leadershipProblemSolving.map((item, index) => (
                  <ScoreItemRow
                    key={index}
                    item={item}
                    index={index}
                    onChange={(i, score) => handleScoreChange('leadershipProblemSolving', i, score)}
                    disabled={isFormDisabled}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CollapsibleSection>

        {/* ============================================ */}
        {/* PART 2: QUARTERLY 1:1 REVIEW SECTIONS */}
        {/* ============================================ */}
        
        <Divider sx={{ my: 4 }}>
          <Chip label="QUARTERLY 1:1 REVIEW" color="primary" />
        </Divider>

        {/* Section A: Performance Key Factors */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Section A: Performance Key Factors
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Rate each behavior using the letter grade scale (A+ to F)
            </Typography>
            
            {(formData.performanceKeyFactors || []).map((category: PEARKeyFactorCategory, catIndex: number) => (
              <Paper key={catIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                  {category.category}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '50%' }}>Behavior</TableCell>
                        <TableCell sx={{ width: '25%' }}>Self Rating</TableCell>
                        <TableCell sx={{ width: '25%' }}>Manager Rating</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(category.subItems || []).map((subItem: PEARKeyFactorSubItem, subIndex: number) => (
                        <TableRow key={subIndex}>
                          <TableCell>
                            <Typography variant="body2">{subItem.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={subItem.selfRating || ''}
                                onChange={(e) => {
                                  const updatedFactors = [...(formData.performanceKeyFactors || [])];
                                  const updatedSubItems = [...(updatedFactors[catIndex].subItems || [])];
                                  updatedSubItems[subIndex] = { ...updatedSubItems[subIndex], selfRating: e.target.value };
                                  updatedFactors[catIndex] = { ...updatedFactors[catIndex], subItems: updatedSubItems };
                                  handleFieldChange('performanceKeyFactors', updatedFactors);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={subItem.managerRating || ''}
                                onChange={(e) => {
                                  const updatedFactors = [...(formData.performanceKeyFactors || [])];
                                  const updatedSubItems = [...(updatedFactors[catIndex].subItems || [])];
                                  updatedSubItems[subIndex] = { ...updatedSubItems[subIndex], managerRating: e.target.value };
                                  updatedFactors[catIndex] = { ...updatedFactors[catIndex], subItems: updatedSubItems };
                                  handleFieldChange('performanceKeyFactors', updatedFactors);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Manager Comments"
              value={formData.performanceKeyFactorsManagerComments || ''}
              onChange={(e) => handleFieldChange('performanceKeyFactorsManagerComments', e.target.value)}
              disabled={isFormDisabled}
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>

        {/* Section B: BSC Reference */}
        <Card sx={{ mb: 3, border: bscForPeriod ? '2px solid #4caf50' : formData.reviewPeriod ? '2px solid #ff9800' : undefined }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Section B: BSC Reference
              </Typography>
              {bscForPeriod ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`BSC ${bscForPeriod.status?.toUpperCase()}`}
                  color="success"
                  size="small"
                />
              ) : formData.reviewPeriod ? (
                <Chip
                  icon={<WarningIcon />}
                  label="BSC Missing"
                  color="warning"
                  size="small"
                />
              ) : null}
            </Stack>

            {/* Warning if BSC is missing for the period */}
            {formData.reviewPeriod && !bscForPeriod && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <WarningIcon fontSize="small" />
                  <Typography variant="body2">
                    <strong>No BSC found for {formData.reviewPeriod}.</strong>{' '}
                    Please complete your Balanced Scorecard for this quarter before the 1:1 review.
                  </Typography>
                </Stack>
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => router.push('/reviews/bsc/new?period=' + formData.reviewPeriod)}
                >
                  Create BSC
                </Button>
              </Alert>
            )}

            {/* BSC Score Display */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {bscForPeriod ? (
                  <Paper elevation={2} sx={{ p: 2, bgcolor: 'success.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      BSC Overall Score
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="success.dark">
                      {bscForPeriod.overallPerformance?.toFixed(1) || bscForPeriod.totalWeightedScore?.toFixed(1) || 'N/A'}%
                    </Typography>
                    {bscForPeriod.finalGrade && (
                      <Chip label={`Grade: ${bscForPeriod.finalGrade}`} size="small" color="success" sx={{ mt: 1 }} />
                    )}
                    <Button
                      size="small"
                      variant="text"
                      sx={{ mt: 1, display: 'block', mx: 'auto' }}
                      onClick={() => router.push(`/reviews/bsc/${bscForPeriod.id}`)}
                    >
                      View BSC Details
                    </Button>
                  </Paper>
                ) : (
                  <Paper elevation={2} sx={{ p: 2, bgcolor: 'warning.light', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      BSC Overall Score
                    </Typography>
                    <Typography variant="h4" color="text.secondary">
                      --
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formData.reviewPeriod ? 'Not submitted' : 'Select period'}
                    </Typography>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Manager Comments on BSC Performance"
                  value={formData.bscManagerComments || ''}
                  onChange={(e) => handleFieldChange('bscManagerComments', e.target.value)}
                  disabled={isFormDisabled}
                  placeholder="Manager: Add comments about the employee's BSC performance..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section C: KRA Setting for Next Quarter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                Section C: KRA (Key Results Area) Setting for Next Quarter
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const updated = [...(formData.nextQuarterKPIs || []), { description: '', measurement: '' }];
                  handleFieldChange('nextQuarterKPIs', updated);
                }}
                disabled={isFormDisabled}
              >
                Add KRA
              </Button>
            </Stack>
            {(formData.nextQuarterKPIs || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No KRAs added yet. Click "Add KRA" to add one.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '45%' }}>KRA Description</TableCell>
                      <TableCell sx={{ width: '45%' }}>How to Measure</TableCell>
                      <TableCell sx={{ width: '10%' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.nextQuarterKPIs || []).map((item: PEARKPIItem, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description || ''}
                            onChange={(e) => {
                              const updated = [...(formData.nextQuarterKPIs || [])];
                              updated[index] = { ...updated[index], description: e.target.value };
                              handleFieldChange('nextQuarterKPIs', updated);
                            }}
                            disabled={isFormDisabled}
                            placeholder="Enter KRA description"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.measurement || ''}
                            onChange={(e) => {
                              const updated = [...(formData.nextQuarterKPIs || [])];
                              updated[index] = { ...updated[index], measurement: e.target.value };
                              handleFieldChange('nextQuarterKPIs', updated);
                            }}
                            disabled={isFormDisabled}
                            placeholder="How will this be measured?"
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const updated = (formData.nextQuarterKPIs || []).filter((_, i) => i !== index);
                              handleFieldChange('nextQuarterKPIs', updated);
                            }}
                            disabled={isFormDisabled}
                          >
                            ×
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Section D: OKRs */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Section D: Objectives & Key Results (OKRs)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Objective"
                  value={formData.okrObjective || ''}
                  onChange={(e) => handleFieldChange('okrObjective', e.target.value)}
                  disabled={isFormDisabled}
                  placeholder="Enter your objective for this quarter"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Challenge Level (1-5)</InputLabel>
                  <Select
                    value={formData.okrObjectiveChallenge ?? ''}
                    label="Challenge Level (1-5)"
                    onChange={(e) => handleFieldChange('okrObjectiveChallenge', e.target.value ? Number(e.target.value) : undefined)}
                    disabled={isFormDisabled}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {OKR_SCALE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="medium">Key Results</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const updated = [...(formData.okrKeyResults || []), {
                    description: '',
                    selfRating: 0,
                    managerPerformanceRating: 0,
                    managerAlignmentRating: 0,
                    managerComment: '',
                  }];
                  handleFieldChange('okrKeyResults', updated);
                }}
                disabled={isFormDisabled}
              >
                Add Key Result
              </Button>
            </Stack>
            
            {(formData.okrKeyResults || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No key results added yet.</Typography>
            ) : (
              (formData.okrKeyResults || []).map((kr: PEAROKRKeyResult, index: number) => (
                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Key Result #{index + 1}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = (formData.okrKeyResults || []).filter((_, i) => i !== index);
                        handleFieldChange('okrKeyResults', updated);
                      }}
                      disabled={isFormDisabled}
                    >
                      ×
                    </IconButton>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Description"
                        value={kr.description || ''}
                        onChange={(e) => {
                          const updated = [...(formData.okrKeyResults || [])];
                          updated[index] = { ...updated[index], description: e.target.value };
                          handleFieldChange('okrKeyResults', updated);
                        }}
                        disabled={isFormDisabled}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Self Rating</InputLabel>
                        <Select
                          value={kr.selfRating || ''}
                          label="Self Rating"
                          onChange={(e) => {
                            const updated = [...(formData.okrKeyResults || [])];
                            updated[index] = { ...updated[index], selfRating: Number(e.target.value) };
                            handleFieldChange('okrKeyResults', updated);
                          }}
                          disabled={isFormDisabled}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {OKR_SCALE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Mgr Performance</InputLabel>
                        <Select
                          value={kr.managerPerformanceRating || ''}
                          label="Mgr Performance"
                          onChange={(e) => {
                            const updated = [...(formData.okrKeyResults || [])];
                            updated[index] = { ...updated[index], managerPerformanceRating: Number(e.target.value) };
                            handleFieldChange('okrKeyResults', updated);
                          }}
                          disabled={isFormDisabled}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {OKR_SCALE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Mgr Alignment</InputLabel>
                        <Select
                          value={kr.managerAlignmentRating || ''}
                          label="Mgr Alignment"
                          onChange={(e) => {
                            const updated = [...(formData.okrKeyResults || [])];
                            updated[index] = { ...updated[index], managerAlignmentRating: Number(e.target.value) };
                            handleFieldChange('okrKeyResults', updated);
                          }}
                          disabled={isFormDisabled}
                        >
                          <MenuItem value=""><em>Select</em></MenuItem>
                          {OKR_SCALE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.value}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Manager Comment"
                        value={kr.managerComment || ''}
                        onChange={(e) => {
                          const updated = [...(formData.okrKeyResults || [])];
                          updated[index] = { ...updated[index], managerComment: e.target.value };
                          handleFieldChange('okrKeyResults', updated);
                        }}
                        disabled={isFormDisabled}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))
            )}
          </CardContent>
        </Card>

        {/* Section E: CIS (Continuous Improvement) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Section E: WP CIS (Continuous Improvement)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  List improvements made this quarter
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const updated = [...(formData.cisImprovements || []), ''];
                  handleFieldChange('cisImprovements', updated);
                }}
                disabled={isFormDisabled}
              >
                Add Improvement
              </Button>
            </Stack>
            {(formData.cisImprovements || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No improvements added yet.</Typography>
            ) : (
              (formData.cisImprovements || []).map((item: string, index: number) => (
                <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => {
                      const updated = [...(formData.cisImprovements || [])];
                      updated[index] = e.target.value;
                      handleFieldChange('cisImprovements', updated);
                    }}
                    disabled={isFormDisabled}
                    placeholder={`Improvement #${index + 1}`}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const updated = (formData.cisImprovements || []).filter((_, i) => i !== index);
                      handleFieldChange('cisImprovements', updated);
                    }}
                    disabled={isFormDisabled}
                  >
                    ×
                  </IconButton>
                </Stack>
              ))
            )}
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Manager Comments"
              value={formData.cisManagerComments || ''}
              onChange={(e) => handleFieldChange('cisManagerComments', e.target.value)}
              disabled={isFormDisabled}
              sx={{ mt: 2 }}
            />
          </CardContent>
        </Card>

        {/* Section F: 360° Review Feedback */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Section F: 360° Review Feedback
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Feedback received from peers, direct reports, and other stakeholders
            </Typography>

            {feedback360Summary ? (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2">
                      <strong>{feedback360Summary.totalReviewers}</strong> feedback response{feedback360Summary.totalReviewers !== 1 ? 's' : ''} received for {formData.reviewPeriod}
                    </Typography>
                    <Chip 
                      label={`Overall: ${feedback360Summary.overallAvg.toFixed(1)} - ${getLabelForRating(feedback360Summary.overallAvg)}`}
                      sx={{ 
                        backgroundColor: getColorForRating(feedback360Summary.overallAvg), 
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Alert>

                {feedback360Summary.sections.map((section, sectionIndex) => (
                  <Paper key={sectionIndex} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {section.title}
                      </Typography>
                      <Chip 
                        label={`Avg: ${section.sectionAvg.toFixed(1)} - ${getLabelForRating(section.sectionAvg)}`}
                        size="small"
                        sx={{ 
                          backgroundColor: getColorForRating(section.sectionAvg), 
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>

                    {/* Rating Summary */}
                    {section.ratingData.length > 0 && (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', width: 100 }}>Avg Rating</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 'bold', width: 80 }}>Responses</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {section.ratingData.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Typography variant="body2">{item.question}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={`${item.avgRating.toFixed(1)}`}
                                    size="small" 
                                    sx={{ 
                                      bgcolor: getColorForRating(item.avgRating),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: 50
                                    }} 
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    {item.responseCount}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Text Responses */}
                    {section.textResponses.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                          Suggestions & Comments:
                        </Typography>
                        {section.textResponses.map((item, idx) => (
                          <Paper 
                            key={idx} 
                            sx={{ 
                              p: 1.5, 
                              mb: 1, 
                              bgcolor: '#f5f5f5',
                              borderLeft: '3px solid #1976d2'
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.question}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              "{item.text}"
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  No 360° feedback received for {formData.reviewPeriod || 'this period'}.
                </Typography>
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Manager's Summary (optional notes based on feedback above)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Manager's Feedback Summary"
              value={formData.feedback360 || ''}
              onChange={(e) => handleFieldChange('feedback360', e.target.value)}
              disabled={isFormDisabled}
              placeholder="Add any additional observations or summary of the 360° feedback..."
            />
          </CardContent>
        </Card>

        {/* Section G: Core Values */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Section G: Core Values
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please rate/comment how the employee exemplified each core value below
            </Typography>

            {/* ACT Values */}
            <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box sx={{ 
                bgcolor: '#1976d2', 
                color: 'white', 
                px: 2, 
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                  <Box component="span" sx={{ 
                    bgcolor: 'white', 
                    color: '#1976d2', 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontWeight: 'bold',
                    mr: 1
                  }}>
                    ACT
                  </Box>
                  Accountable • Community • Transparent
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                      <TableCell sx={{ width: '10%', fontWeight: 'bold' }}></TableCell>
                      <TableCell sx={{ width: '45%', fontWeight: 'bold' }}>Core Value</TableCell>
                      <TableCell sx={{ width: '22.5%', fontWeight: 'bold' }}>Self Rating</TableCell>
                      <TableCell sx={{ width: '22.5%', fontWeight: 'bold' }}>Manager Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.coreValues || []).filter((item: PEARCoreValueItem) => item.group === 'ACT').map((item: PEARCoreValueItem, index: number) => {
                      const actualIndex = (formData.coreValues || []).findIndex((cv: PEARCoreValueItem) => cv.value === item.value);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip 
                              label={item.acronym} 
                              size="small" 
                              sx={{ 
                                bgcolor: '#1976d2', 
                                color: 'white', 
                                fontWeight: 'bold',
                                minWidth: 32
                              }} 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{item.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.selfRating || ''}
                                onChange={(e) => {
                                  const updated = [...(formData.coreValues || [])];
                                  updated[actualIndex] = { ...updated[actualIndex], selfRating: e.target.value };
                                  handleFieldChange('coreValues', updated);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.managerRating || ''}
                                onChange={(e) => {
                                  const updated = [...(formData.coreValues || [])];
                                  updated[actualIndex] = { ...updated[actualIndex], managerRating: e.target.value };
                                  handleFieldChange('coreValues', updated);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* REF Values */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              <Box sx={{ 
                bgcolor: '#2e7d32', 
                color: 'white', 
                px: 2, 
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'white' }}>
                  <Box component="span" sx={{ 
                    bgcolor: 'white', 
                    color: '#2e7d32', 
                    px: 1.5, 
                    py: 0.5, 
                    borderRadius: 1,
                    fontWeight: 'bold',
                    mr: 1
                  }}>
                    REF
                  </Box>
                  Reputable • Execution • Flexible
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell sx={{ width: '10%', fontWeight: 'bold' }}></TableCell>
                      <TableCell sx={{ width: '45%', fontWeight: 'bold' }}>Core Value</TableCell>
                      <TableCell sx={{ width: '22.5%', fontWeight: 'bold' }}>Self Rating</TableCell>
                      <TableCell sx={{ width: '22.5%', fontWeight: 'bold' }}>Manager Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(formData.coreValues || []).filter((item: PEARCoreValueItem) => item.group === 'REF').map((item: PEARCoreValueItem, index: number) => {
                      const actualIndex = (formData.coreValues || []).findIndex((cv: PEARCoreValueItem) => cv.value === item.value);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip 
                              label={item.acronym} 
                              size="small" 
                              sx={{ 
                                bgcolor: '#2e7d32', 
                                color: 'white', 
                                fontWeight: 'bold',
                                minWidth: 32
                              }} 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">{item.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.selfRating || ''}
                                onChange={(e) => {
                                  const updated = [...(formData.coreValues || [])];
                                  updated[actualIndex] = { ...updated[actualIndex], selfRating: e.target.value };
                                  handleFieldChange('coreValues', updated);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={item.managerRating || ''}
                                onChange={(e) => {
                                  const updated = [...(formData.coreValues || [])];
                                  updated[actualIndex] = { ...updated[actualIndex], managerRating: e.target.value };
                                  handleFieldChange('coreValues', updated);
                                }}
                                disabled={isFormDisabled}
                                displayEmpty
                              >
                                <MenuItem value=""><em>Select</em></MenuItem>
                                {LETTER_GRADE_OPTIONS.map((grade) => (
                                  <MenuItem key={grade} value={grade}>{grade}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </CardContent>
        </Card>

        {/* Section H: Personal Development */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Section H: Personal Development
            </Typography>
            
            {/* Strengths */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="medium">Strengths</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const updated = [...(formData.strengths || []), ''];
                    handleFieldChange('strengths', updated);
                  }}
                  disabled={isFormDisabled}
                >
                  Add Strength
                </Button>
              </Stack>
              {(formData.strengths || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No strengths added yet.</Typography>
              ) : (
                (formData.strengths || []).map((item: string, index: number) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => {
                        const updated = [...(formData.strengths || [])];
                        updated[index] = e.target.value;
                        handleFieldChange('strengths', updated);
                      }}
                      disabled={isFormDisabled}
                      placeholder={`Strength #${index + 1}`}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = (formData.strengths || []).filter((_, i) => i !== index);
                        handleFieldChange('strengths', updated);
                      }}
                      disabled={isFormDisabled}
                    >
                      ×
                    </IconButton>
                  </Stack>
                ))
              )}
            </Box>

            {/* Development Needs */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight="medium">Development Needs</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const updated = [...(formData.developmentNeeds || []), ''];
                    handleFieldChange('developmentNeeds', updated);
                  }}
                  disabled={isFormDisabled}
                >
                  Add Need
                </Button>
              </Stack>
              {(formData.developmentNeeds || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No development needs added yet.</Typography>
              ) : (
                (formData.developmentNeeds || []).map((item: string, index: number) => (
                  <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={item}
                      onChange={(e) => {
                        const updated = [...(formData.developmentNeeds || [])];
                        updated[index] = e.target.value;
                        handleFieldChange('developmentNeeds', updated);
                      }}
                      disabled={isFormDisabled}
                      placeholder={`Development need #${index + 1}`}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = (formData.developmentNeeds || []).filter((_, i) => i !== index);
                        handleFieldChange('developmentNeeds', updated);
                      }}
                      disabled={isFormDisabled}
                    >
                      ×
                    </IconButton>
                  </Stack>
                ))
              )}
            </Box>

            {/* Manager Development Support */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Manager Development Support Plan"
              value={formData.managerDevelopmentSupport || ''}
              onChange={(e) => handleFieldChange('managerDevelopmentSupport', e.target.value)}
              disabled={isFormDisabled}
              placeholder="How will the manager support the employee's development?"
            />
          </CardContent>
        </Card>

        {/* Final Section: Overall Manager Rating */}
        <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Overall Manager Assessment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Overall Rating (1-10)</InputLabel>
                  <Select
                    value={formData.overallManagerRating ?? ''}
                    label="Overall Rating (1-10)"
                    onChange={(e) => handleFieldChange('overallManagerRating', e.target.value ? Number(e.target.value) : undefined)}
                    disabled={isFormDisabled}
                  >
                    <MenuItem value=""><em>Select</em></MenuItem>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Overall Manager Comments"
                  value={formData.overallManagerComments || ''}
                  onChange={(e) => handleFieldChange('overallManagerComments', e.target.value)}
                  disabled={isFormDisabled}
                  placeholder="Summarize overall performance and recommendations..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button variant="outlined" onClick={() => router.push('/reviews/pear')}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSave(true)}
            disabled={isLoading || isViewOnly || !isReviewPeriodSet}
          >
            Save Draft
          </Button>
          <Tooltip title={!canSubmitQuarter ? quarterSubmissionMessage : (!canSubmitPEAR ? 'Only the employee can submit their own PEAR Review' : '')} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSubmit}
                disabled={isLoading || isViewOnly || !isReviewPeriodSet || !canSubmitQuarter || !canSubmitPEAR}
              >
                Submit for Approval
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </PageWithHeader>
  );
};

export default PEARReviewForm;
